import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, payload } from "./lib/api";
import { LeadReview } from "./LeadReview";

interface LeadSummary {
  discoveredToday: number;
  total: number;
  noWebsite: number;
  withWebsite: number;
}

interface LeadListItem {
  id: string;
  companyName: string;
  category: string | null;
  city: string | null;
  province: string | null;
  website: string | null;
  digitalIdentity: "HAS_WEBSITE" | "NO_DIGITAL_IDENTITY";
  source: string;
  status: string;
  provenanceComplete: boolean;
  createdAt: string;
}

interface LeadDetail extends LeadListItem {
  address: string | null;
  googleBusinessUrl: string | null;
  socialUrl: string | null;
  sourceDetail: string | null;
  notes: string | null;
  reviewCount: number | null;
  rating: number | null;
  confidence: string | null;
  contactName: string | null;
  sourceUrl: string | null;
  sourceCollectedAt: string | null;
  batchId: string | null;
  legalEntityType: string | null;
  emailSourceUrl: string | null;
  contactBasis: string | null;
  email: string | null;
  phone: string | null;
  updatedAt: string;
}

interface LeadEvent {
  id: string;
  type: string;
  createdAt: string;
  payload: { from?: string; to?: string; source?: string };
}

const STATUSES: Array<[string, string]> = [
  ["DISCOVERED", "Trovata"],
  ["ANALYZED", "Analizzata"],
  ["QUALIFIED", "Qualificata"],
  ["DRAFT_READY", "Bozza pronta"],
  ["CONTACTED", "Contattata"],
  ["FOLLOW_UP", "Follow-up"],
  ["REPLIED", "Ha risposto"],
  ["INTERESTED", "Interessata"],
  ["CALL_BOOKED", "Call fissata"],
  ["PROPOSAL", "Proposta"],
  ["WON", "Cliente"],
  ["LOST", "Persa"],
];

function statusLabel(status: string): string {
  return STATUSES.find(([value]) => value === status)?.[1] || status;
}

function formatWhen(value: string): string {
  return new Intl.DateTimeFormat("it", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

export default function LeadsPage() {
  const { leadId } = useParams();
  return leadId ? <LeadDetailView id={leadId} /> : <LeadList />;
}

function LeadList() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<LeadSummary | null>(null);
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("");
  const [identity, setIdentity] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState<"none" | "manual" | "csv">("none");

  const load = useCallback((filters: { query?: string; city?: string; identity?: string } = {}) => {
    const params = new URLSearchParams();
    if (filters.query?.trim()) params.set("q", filters.query.trim());
    if (filters.city?.trim()) params.set("city", filters.city.trim());
    if (filters.identity) params.set("identity", filters.identity);
    Promise.all([
      api<LeadSummary>("/api/leads/summary"),
      api<{ leads: LeadListItem[]; total: number }>(`/api/leads?${params.toString()}`),
    ])
      .then(([nextSummary, list]) => {
        setSummary(nextSummary);
        setLeads(list.leads);
        setTotal(list.total);
        setError("");
      })
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function loadCurrentFilters() {
    load({ query, city, identity });
  }

  return (
    <>
      <header className="topbar">
        <div>
          <p className="eyebrow indigo">Universe Leads</p>
          <h1>Aziende</h1>
        </div>
        <div className="leads-actions">
          <button className="secondary" onClick={() => setComposer(composer === "csv" ? "none" : "csv")}>Importa CSV</button>
          <button className="primary" onClick={() => setComposer(composer === "manual" ? "none" : "manual")}>Nuova azienda</button>
        </div>
      </header>
      <main className="content">
        <p className="leads-note">Ricerca, analisi commerciale e bozze Gmail. Ogni lead qualificato richiede prove verificabili e il confronto con almeno due concorrenti. Il sistema crea soltanto bozze: l’invio resta manuale.</p>
        {summary && (
          <div className="leads-kpis">
            <span><strong>{summary.discoveredToday}</strong>Trovate oggi</span>
            <span><strong>{summary.total}</strong>In archivio</span>
            <span><strong>{summary.noWebsite}</strong>Senza sito</span>
            <span><strong>{summary.withWebsite}</strong>Con sito</span>
          </div>
        )}
        {composer === "manual" && <ManualForm onDone={() => { setComposer("none"); loadCurrentFilters(); }} />}
        {composer === "csv" && <CsvImport onImported={loadCurrentFilters} />}
        <form className="leads-filters" onSubmit={(event) => { event.preventDefault(); loadCurrentFilters(); }}>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nome attività" aria-label="Cerca per nome" />
          <input value={city} onChange={(event) => setCity(event.target.value)} placeholder="Città" aria-label="Filtra per città" />
          <select value={identity} onChange={(event) => setIdentity(event.target.value)} aria-label="Presenza digitale">
            <option value="">Tutte</option>
            <option value="NO_DIGITAL_IDENTITY">Senza sito</option>
            <option value="HAS_WEBSITE">Con sito</option>
          </select>
          <button className="secondary" type="submit">Filtra</button>
        </form>
        {error && <p className="leads-error">{error}</p>}
        {loading ? <p className="leads-note">Caricamento…</p> : (
          <>
            <p className="leads-count">{total} {total === 1 ? "azienda" : "aziende"}</p>
            <div className="leads-table">
              {leads.length === 0 && <p className="leads-note">Nessuna azienda con questi filtri. Importa il CSV oppure inseriscine una.</p>}
              {leads.map((lead) => (
                <button key={lead.id} className="leads-row" onClick={() => navigate(`/leads/${lead.id}`)}>
                  <strong>{lead.companyName}</strong>
                  <span>{[lead.category, lead.city].filter(Boolean).join(" · ") || "Senza categoria"}</span>
                  <em>{lead.digitalIdentity === "HAS_WEBSITE" ? "Con sito" : "Senza sito"}</em>
                  <small>{statusLabel(lead.status)}{lead.provenanceComplete ? "" : " · provenienza incompleta"}</small>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
    </>
  );
}

function ManualForm({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await api("/api/leads", payload("POST", Object.fromEntries(form.entries())));
      onDone();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="leads-form" onSubmit={submit}>
      <label>Nome attività<input name="companyName" required minLength={2} /></label>
      <label>Categoria<input name="category" placeholder="Ristoranti" /></label>
      <label>Città<input name="city" placeholder="Pesaro" /></label>
      <label>Indirizzo<input name="address" /></label>
      <label>Telefono<input name="phone" /></label>
      <label>Email<input name="email" type="email" /></label>
      <label>Sito o social<input name="website" placeholder="vuoto se non ha un sito proprio" /></label>
      <label>Referente<input name="contactName" /></label>
      <label>URL della fonte<input name="sourceUrl" type="url" required placeholder="https://…" /></label>
      <label>Data in cui hai guardato la fonte<input name="sourceCollectedAt" type="date" required /></label>
      <label>Pagina pubblica dell’email<input name="emailSourceUrl" type="url" placeholder="obbligatoria se c’è un’email" /></label>
      <label>Forma giuridica
        <select name="legalEntity" defaultValue="unknown">
          <option value="unknown">Non nota</option>
          <option value="company">Società</option>
          <option value="sole_proprietor">Ditta individuale</option>
        </select>
      </label>
      <label>Base del contatto
        <select name="contactBasis" defaultValue="unknown">
          <option value="unknown">Non nota</option>
          <option value="public_business_email_on_own_site">Email pubblica sul sito dell’attività</option>
          <option value="contact_form_only">Solo modulo di contatto</option>
          <option value="phone_only">Solo telefono</option>
        </select>
      </label>
      {error && <p className="leads-error">{error}</p>}
      <button className="primary" disabled={busy}>{busy ? "Salvo…" : "Salva azienda"}</button>
    </form>
  );
}

function CsvImport({ onImported }: { onImported: () => void }) {
  const [csv, setCsv] = useState("");
  const [label, setLabel] = useState("");
  const [sourceKind, setSourceKind] = useState("manual_research");
  const [sourceDescription, setSourceDescription] = useState("");
  const [error, setError] = useState("");
  const [report, setReport] = useState("");
  const [problems, setProblems] = useState<Array<{ line: number; result: string; detail?: string }>>([]);
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{
        created: number; duplicates: number; suppressed: number; invalid: number;
        rows: Array<{ line: number; result: string; detail?: string }>;
      }>("/api/leads/import", payload("POST", { csv, batch: { label, sourceKind, sourceDescription } }));
      setReport(`Create ${result.created}. Duplicati ${result.duplicates}. Bloccate ${result.suppressed}. Non valide ${result.invalid}.`);
      setProblems(result.rows);
      if (result.created) onImported();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Import non riuscito");
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="leads-form" onSubmit={submit}>
      <label>Nome del lotto<input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Ristoranti Pesaro, ottobre 2026" required /></label>
      <label>Tipo di fonte
        <select value={sourceKind} onChange={(event) => setSourceKind(event.target.value)}>
          <option value="manual_research">Ricerca manuale su pagine pubbliche</option>
          <option value="manual_csv">CSV verificato a mano</option>
        </select>
      </label>
      <label>Cosa è stato guardato e come<input value={sourceDescription} onChange={(event) => setSourceDescription(event.target.value)} required /></label>
      <label>
        CSV
        <textarea value={csv} onChange={(event) => setCsv(event.target.value)} rows={8} placeholder="Città, Categoria, Nome attività, Telefono, Indirizzo, Email, Social" required />
      </label>
      <input type="file" accept=".csv,text/csv" aria-label="Scegli un file CSV" onChange={async (event) => {
        const file = event.target.files?.[0];
        if (file) setCsv(await file.text());
      }} />
      <p className="leads-note">Ogni riga deve avere fonte_url e data_raccolta; se c’è un’email serve anche fonte_email_url. Vanno bene virgola o punto e virgola. Modello: pilot-ristoranti-pesaro-template.csv.</p>
      {error && <p className="leads-error">{error}</p>}
      {report && <p className="leads-note">{report}</p>}
      {problems.length > 0 && (
        <ul className="leads-note">
          {problems.map((item, index) => <li key={index}>Riga {item.line}: {item.result}{item.detail ? `, ${item.detail}` : ""}</li>)}
        </ul>
      )}
      <button className="primary" disabled={busy || csv.trim().length < 8 || !label.trim() || !sourceDescription.trim()}>{busy ? "Importo…" : "Importa"}</button>
    </form>
  );
}

function LeadDetailView({ id }: { id: string }) {
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(() => {
    api<{ lead: LeadDetail; events: LeadEvent[] }>(`/api/leads/${id}`)
      .then((result) => {
        setLead(result.lead);
        setEvents(result.events);
        setStatus(result.lead.status);
      })
      .catch((reason: Error) => setError(reason.message));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function changeStatus(next: string) {
    setStatus(next);
    try {
      await api(`/api/leads/${id}`, payload("PATCH", { status: next }));
      load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Aggiornamento non riuscito");
    }
  }

  if (error && !lead) return <main className="content"><p className="leads-error">{error}</p><Link to="/leads">Torna alle aziende</Link></main>;
  if (!lead) return <main className="content"><p className="leads-note">Caricamento…</p></main>;

  return (
    <>
      <header className="topbar">
        <div>
          <p className="eyebrow indigo"><Link to="/leads">Universe Leads</Link></p>
          <h1>{lead.companyName}</h1>
        </div>
        <select value={status} onChange={(event) => changeStatus(event.target.value)} aria-label="Stato pipeline">
          {STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </header>
      <main className="content leads-detail">
        {error && <p className="leads-error">{error}</p>}
        <section>
          <h2>Dati</h2>
          <dl>
            <div><dt>Categoria</dt><dd>{lead.category || "Non indicata"}</dd></div>
            <div><dt>Città</dt><dd>{[lead.city, lead.province].filter(Boolean).join(" ") || "Non indicata"}</dd></div>
            <div><dt>Indirizzo</dt><dd>{lead.address || "Non indicato"}</dd></div>
            <div><dt>Telefono</dt><dd>{lead.phone || "Non indicato"}</dd></div>
            <div><dt>Email</dt><dd>{lead.email || "Non indicata"}</dd></div>
            <div><dt>Referente</dt><dd>{lead.contactName || "Non indicato"}</dd></div>
            <div><dt>Sito</dt><dd>{lead.website || "Nessun sito proprio"}</dd></div>
            <div><dt>Social</dt><dd>{lead.socialUrl || "Non indicato"}</dd></div>
            <div><dt>Fonte</dt><dd>{[lead.source, lead.sourceDetail].filter(Boolean).join(" · ")}</dd></div>
          </dl>
          <h2>Provenienza</h2>
          {!lead.sourceCollectedAt || !lead.sourceUrl ? (
            <p className="leads-error">Provenienza incompleta: questo lead non può essere analizzato.</p>
          ) : (
            <dl>
              <div><dt>Pagina della fonte</dt><dd><a href={lead.sourceUrl} target="_blank" rel="noopener noreferrer">{lead.sourceUrl}</a></dd></div>
              <div><dt>Guardata il</dt><dd>{lead.sourceCollectedAt}</dd></div>
              <div><dt>Lotto</dt><dd>{lead.batchId || "Inserimento manuale"}</dd></div>
              <div><dt>Forma giuridica</dt><dd>{lead.legalEntityType || "Non nota"}</dd></div>
              <div><dt>Base del contatto</dt><dd>{lead.contactBasis || "Non nota"}</dd></div>
              <div><dt>Pagina dell’email</dt><dd>{lead.emailSourceUrl ? <a href={lead.emailSourceUrl} target="_blank" rel="noopener noreferrer">{lead.emailSourceUrl}</a> : "Nessuna email"}</dd></div>
            </dl>
          )}
          {lead.notes && <p>{lead.notes}</p>}
        </section>
        <section>
          <h2>Presenza digitale</h2>
          <p>{lead.digitalIdentity === "HAS_WEBSITE" ? "Ha un dominio proprio. L’audit parte dalla prossima fase." : "Nessuna identità digitale proprietaria. L’audit del sito non parte."}</p>
        </section>
        <LeadReview leadId={id} hasEmail={Boolean(lead.email)} onChanged={load} />
        <section>
          <h2>Timeline</h2>
          <ol>
            {events.map((event) => (
              <li key={event.id}>
                <strong>{event.type === "status_changed" ? `Da ${statusLabel(event.payload.from || "")} a ${statusLabel(event.payload.to || "")}` : event.type === "imported" ? "Importata da CSV" : "Inserita a mano"}</strong>
                <small>{formatWhen(event.createdAt)}</small>
              </li>
            ))}
          </ol>
        </section>
      </main>
    </>
  );
}
